/**
 * Browser-side client for the Lazada token refresh.
 *
 * No Lazada credential lives here and none ever may — everything VITE_-prefixed
 * is compiled into the public bundle. The app secret stays in the Function;
 * this only carries the refresh token there and brings the answer back.
 */
import { getApiTokenSilent } from "../../shell/auth/tokens";
import { apiAuthHeaders } from "../../lib/apiAuthHeaders";

/** What /api/lazada/refresh answers with: absolute instants in unix ms. */
export interface ILazadaPair {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: number;
  refreshExpiresAt: number;
  account: string;
  country: string;
}

async function readError(res: Response): Promise<string> {
  const raw = await res.text().catch(() => "");
  try {
    const parsed = JSON.parse(raw) as { error?: string };
    if (parsed.error) return parsed.error;
  } catch {
    /* Not our JSON — fall through and show what did arrive. */
  }
  const snippet = raw.trim().slice(0, 200);
  return snippet
    ? `Request failed (${res.status}). ${snippet}`
    : `Request failed (${res.status}) with an empty body — the request was probably cut off by `
      + "the platform's time limit rather than refused by Lazada.";
}

/**
 * Trade a refresh token for a fresh pair.
 *
 * THE ANSWER MUST BE STORED BEFORE ANYTHING USES IT. Lazada rotates the
 * refresh token, so the one passed in here is dead the instant this resolves —
 * if the caller drops the answer on the floor, the live credential exists
 * nowhere and somebody has to re-authorise by hand.
 */
export async function refreshLazadaTokens(refreshToken: string): Promise<ILazadaPair> {
  const token = await getApiTokenSilent();
  const res = await fetch("/api/lazada/refresh", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "content-type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as ILazadaPair;
}

/** One order as /api/lazada/orders returns it. */
export interface ILazadaApiOrder {
  id: string;
  number: string;
  createdAt: string;
  status: string;
  statuses: string[];
  total: number;
  currency: string;
  items: number;
  city: string;
  province: string;
  paymentMethod: string;
}

export interface ILazadaOrdersAnswer {
  asOf: string;
  truncated: boolean;
  orders: ILazadaApiOrder[];
  oldest: string;
}

/**
 * Walk the live order ledger.
 *
 * The access token travels in the BODY, not the query: a query string is
 * written to every access log, proxy trace and browser history on the way.
 * `days = 0` means ALL TIME and omits the window entirely — the same meaning
 * IG COM and TikTok give it, deliberately.
 */
export async function fetchLazadaOrders(opts: {
  accessToken: string;
  days?: number;
  /** Resume cursor: only orders created strictly BEFORE this ISO instant. */
  before?: string;
}): Promise<ILazadaOrdersAnswer> {
  const token = await getApiTokenSilent();
  const res = await fetch("/api/lazada/orders", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "content-type": "application/json" },
    body: JSON.stringify({
      accessToken: opts.accessToken,
      days: opts.days ?? 0,
      before: opts.before || undefined,
    }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as ILazadaOrdersAnswer;
}

/**
 * Change an order's state at Lazada.
 *
 * THERE IS NO UNDO. The caller confirms with the order reference and the
 * amount in front of the person before calling this — see fulfilActions.
 */
export async function lazadaFulfil(opts: {
  accessToken: string;
  action: "rts" | "packed";
  orderId: string;
}): Promise<{ ok: true; orderId: string; items: number }> {
  const token = await getApiTokenSilent();
  const res = await fetch("/api/lazada/fulfil", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as { ok: true; orderId: string; items: number };
}
